import { useState } from 'react'
import * as XLSX from 'xlsx'
import { CloudUpload, FileSpreadsheet, Download, AlertCircle } from 'lucide-react'
import { sileo } from 'sileo'
// import ModalTemplatesProgramadosIndex from './modalTemplate/Index'
import { excelRowsToScheduledDocs } from './utils/excelParser'
import { FacturaAPI } from '../../../../../../../../api/facturas.api'

export default function Paso0SubirExcelProgramados({ onUploadSuccess, isModal = false }) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [fileName, setFileName] = useState('')
  // const [openTemplates, setOpenTemplates] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)

  const downloadFacturasTemplate = async () => {
    if (isDownloadingTemplate) return

    setIsDownloadingTemplate(true)

    try {
      const facturasAPI = new FacturaAPI()
      const response = await facturasAPI.downloadExcelTemplate('scheduled', 'CLP', 'taxable')

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')

      link.href = url
      link.download = 'plantilla_facturas_programadas_33_34_CLP.xlsx'

      document.body.appendChild(link)
      link.click()

      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error al descargar plantilla:', error)
      sileo.error({
        title: 'Error de Descarga',
        description: 'No se pudo descargar la plantilla. Intenta nuevamente'
      })
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const processFile = file => {
    if (!file) return

    setFileName('')
    setValidationErrors([])
    setIsProcessing(true)

    const reader = new FileReader()
    reader.onload = async event => {
      try {
        const data = new Uint8Array(event.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        const sheetName =
          workbook.SheetNames.find(n => n.toLowerCase() === 'documentos_programados') ||
          workbook.SheetNames.find(n => n.toLowerCase().includes('program')) ||
          workbook.SheetNames[0]

        const sheet = workbook.Sheets[sheetName]
        const json = XLSX.utils.sheet_to_json(sheet, { defval: '', cellDates: true })

        if (!json.length) {
          sileo.warning({
            title: 'Hoja Vacía',
            description: 'La hoja seleccionada está vacía 🤯'
          })
          setFileName('')
          return
        }

        // --- VALIDACIÓN ---
        const { documents, report } = excelRowsToScheduledDocs(json)

        if (report.failed > 0) {
          const allErrors = report.errorDetails.flatMap(d => d.errors)
          const uniqueErrors = Array.from(new Set(allErrors))
          setValidationErrors(uniqueErrors)
          setFileName('') // Limpiar nombre si hay errores
          return
        }

        setFileName(file.name)
        await onUploadSuccess({ documents, fileName: file.name })
      } catch (err) {
        console.error(err)
        sileo.error({
          title: 'Error de Lectura',
          description: 'No se pudo leer el archivo Excel 🤯'
        })
      } finally {
        setIsProcessing(false)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFileUpload = e => {
    const file = e.target.files?.[0]
    processFile(file)
    e.target.value = ''
  }

  const handleDragOver = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (
      file &&
      (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls'))
    ) {
      processFile(file)
    } else {
      sileo.error({
        title: 'Formato no soportado',
        description: 'Por favor, sube un archivo Excel (.xlsx o .xls)'
      })
    }
  }

  return (
    <div className='flex h-full flex-col bg-slate-50/30'>
      <div className='flex-1 overflow-y-auto px-4 py-4'>
        <div className='max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-5 items-stretch'>
          {/* Bloque Izquierdo: Descarga */}
          <div className='flex flex-col rounded-xl bg-white border border-slate-200 p-5 shadow-sm transition-all hover:shadow-md'>
            <div>
              <div className='flex'>
                <div className='flex flex-col'>
                  <h4 className='text-base font-bold text-slate-900 mb-1.5'>
                    Plantilla de programación
                  </h4>
                  <p className='text-xs text-slate-500 leading-normal mb-2'>
                    Descarga la plantilla, complétala y súbela para continuar con la emisión.
                  </p>
                </div>
              </div>

              {/* <button
                type='button'
                onClick={() => setOpenTemplates(true)}
                className='inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-100 px-5 py-2.5 text-xs font-bold text-slate-900 transition-all hover:bg-slate-200 active:scale-[0.98] border border-gray-200'
              >
                <Download size={16} />
                Descargar plantilla
              </button> */}

              <button
                type='button'
                onClick={downloadFacturasTemplate}
                disabled={isDownloadingTemplate}
                className='inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-100 px-5 py-2.5 text-xs font-bold text-slate-900 transition-all hover:bg-slate-200 active:scale-[0.98] border border-gray-200 disabled:opacity-60 disabled:cursor-not-allowed'
              >
                <Download size={16} />
                {isDownloadingTemplate ? 'Descargando...' : 'Descargar plantilla'}
              </button>
            </div>

            {/* Bloque Informativo: Ayuda Preventiva */}
            <div className='mt-6 rounded-xl bg-[#EFF6FF] border border-slate-100 p-4'>
              <p className='text-[11px] mb-4 leading-relaxed font-medium text-blue-900'>
                Descarga la plantilla y complétala con los datos de facturación. Asegúrate de que:
              </p>

              <ul className='space-y-2'>
                {[
                  'Usa la hoja "Documentos Programados"',
                  'El archivo debe estar en formato .xlsx o .xls',
                  'Respeta los encabezados sugeridos en la plantilla',
                  'Los datos se validarán en el siguiente paso'
                ].map((item, idx) => (
                  <li key={idx} className='flex items-start gap-2 text-[11px] text-blue-800'>
                    <div className='mt-0.5 flex-shrink-0'>
                      <div className='h-1 w-1 rounded-full bg-blue-400 flex items-center justify-center' />
                    </div>
                    <span className='leading-tight'>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bloque Derecho: Subida */}
          <div className='flex flex-col rounded-xl bg-white border border-accent2/20 p-5 shadow-[0_5px_15px_rgba(242,108,34,0.04)] relative overflow-hidden'>
            <div className='absolute top-0 right-0 p-3 opacity-5 pointer-events-none'>
              <FileSpreadsheet size={60} />
            </div>

            <div className='relative z-10 flex-1 flex flex-col'>
              <h4 className='text-base font-bold text-slate-900 mb-1.5'>Subir archivo</h4>
              <p className='text-xs text-slate-500 leading-normal mb-5'>
                Selecciona la planilla para revisar las programaciones antes de crearlas.
              </p>

              <div
                className={`flex-1 flex flex-col items-center justify-center rounded-lg border-2 border-dashed transition-all duration-300 p-6 ${
                  isDragging
                    ? 'border-accent2 bg-orange-50/50'
                    : 'border-slate-200 bg-slate-50/50 hover:bg-slate-50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center mb-3 transition-transform duration-300 ${
                    isDragging
                      ? 'scale-110 bg-accent2 text-white'
                      : 'bg-white text-slate-400 shadow-sm'
                  }`}
                >
                  <CloudUpload size={22} />
                </div>

                <p className='text-xs font-semibold text-slate-900 text-center mb-4'>
                  Arrastra o haz clic aquí para seleccionar
                </p>

                <input
                  id='excel-upload-programados'
                  type='file'
                  accept='.xls,.xlsx'
                  onChange={handleFileUpload}
                  className='sr-only'
                />

                <label
                  htmlFor='excel-upload-programados'
                  className='cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-black/20 transition-all hover:bg-accent2/90 active:scale-95'
                >
                  <FileSpreadsheet size={16} />
                  Seleccionar archivo
                </label>
              </div>

              {validationErrors.length > 0 && (
                <div className='mt-3 rounded-xl border border-rose-100 bg-rose-50/70 py-2 px-3 transition-all animate-in fade-in slide-in-from-top-2'>
                  <div className='flex items-center gap-2.5 mb-2'>
                    <div className='flex items-center justify-center h-6 w-6 rounded-full bg-rose-100 text-rose-600'>
                      <AlertCircle size={15} />
                    </div>
                    <h5 className='text-sm font-bold text-rose-900'>
                      Se encontraron errores en el archivo
                    </h5>
                  </div>
                  <p className='text-[11px] text-rose-700/80 ml-8'>
                    Corrige los siguientes datos y vuelve a cargar la plantilla.
                  </p>
                  <ul className='ml-8 max-h-36 overflow-y-auto pr-1'>
                    {validationErrors.map((msg, i) => (
                      <li
                        key={i}
                        className='flex items-center gap-2 text-[11px] text-rose-800 font-semibold'
                      >
                        <div className='h-1 w-1 rounded-full bg-rose-400 flex-shrink-0' />
                        {msg}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className='mt-3 flex flex-col gap-2'>
                <div
                  className={`rounded-xl border px-3 py-2 text-xs transition-all flex items-center gap-3 ${
                    fileName
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-slate-200 bg-slate-50 text-slate-400'
                  }`}
                >
                  <div
                    className={`h-1.5 w-1.5 rounded-full ${
                      fileName ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'
                    }`}
                  />
                  {fileName ? `Archivo: ${fileName}` : 'No seleccionado'}
                </div>

                {isProcessing && (
                  <div className='rounded-xl border border-slate-300 bg-white px-3 py-2 flex items-center gap-3'>
                    <div className='h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin' />
                    <span className='text-xs font-medium text-slate-700'>Procesando...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <ModalTemplatesProgramadosIndex open={openTemplates} onClose={() => setOpenTemplates(false)} /> */}
    </div>
  )
}
